// src/composables/useGlobalHotkeys.js
import { onMounted, onUnmounted, computed } from "@vue/composition-api";
import store from "@/store";
import router from "@/router";
import { isUserLoggedIn } from "@/auth/utils";
import { usePickAndPlace } from "@/composables/usePickAndPlace";
import LayoutApi from "@/libs/LayoutApi.js";
import { SLOT_TYPES } from "@/config/layoutConfig";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import moment from "moment-timezone";
import ability from "@/libs/acl/ability";


/**
 * Definición central de hotkeys
 * - action: string identificador o función directa
 * - requiresAuth: necesita estar loggeado (default: true)
 * - ability: { action, subject } para validar
 * - ignoreInInputs: no disparar si estamos en un input (default: true)
 */

const { startPick, cancelPick, isPickMode } = usePickAndPlace();
const layoutApi = new LayoutApi(store);

function getLast30DaysRange() {
  return {
    start: moment().subtract(30, "days").format("YYYY-MM-DD HH:mm:ss"),
    end: moment().format("YYYY-MM-DD HH:mm:ss"),
  };
}

function getOrderedTabIds(tabs) {
  if (!tabs || typeof tabs !== "object") return [];
  return Object.entries(tabs)
    .sort((a, b) => (Number(a[1].order) || 0) - (Number(b[1].order) || 0))
    .map(([id]) => id);
}

// Busca la alarma en atención: primero el tab activo, si no cualquier tab de alarma
function findAttendingAlarm(store) {
  const tabs = store.state.grid.tabs || {};
  const activeTab = tabs[store.state.grid.activeTab];
  const alarmTab = activeTab?.context?.isAlarm
    ? activeTab
    : Object.values(tabs).find((tab) => tab?.context?.isAlarm);
  if (!alarmTab) return null;
  const infoSlot = (alarmTab.slots || []).find((slot) => slot.type === SLOT_TYPES.INFO);
  const raw = infoSlot?.data;
  if (!raw) return null;
  // El slot INFO guarda la alarma directo en data, pero algunos flujos la anidan en data.data
  return raw.unit ? raw : (raw.data?.unit ? raw.data : raw);
}

// La instalacion seleccionada en el arbol es el foco explicito. Si no hay una,
// se conserva el comportamiento anterior usando la alarma en atencion.
function findFocusedUnit(store) {
  const facility = store.state.grid.focusedFacility;
  const facilityId = facility?.unit_data?.id ?? facility?.unit_data?.id_unit ?? facility?.id;
  if (facilityId != null) {
    const unitFromStore = (store.state.grid.units || []).find(
      unit => String(unit.id) === String(facilityId)
    );
    if (unitFromStore) return unitFromStore;
    return facility.unit_data
      ? { ...facility.unit_data, id: facility.unit_data.id ?? facilityId }
      : facility;
  }

  return findAttendingAlarm(store)?.unit || null;
}

function getFocusedUnitEmergencyContacts(store) {
  const unit = findFocusedUnit(store);
  if (!unit) return { contacts: [], unitName: "" };

  let contacts = Array.isArray(unit.emergency_contact)
    ? unit.emergency_contact
    : Array.isArray(unit.emergency_contacts)
    ? unit.emergency_contacts
    : [];

  if (contacts.length === 0) {
    const unitFromStore = (store.state.grid.units || []).find(
      (u) => String(u.id) === String(unit.id)
    );
    contacts = Array.isArray(unitFromStore?.emergency_contacts)
      ? unitFromStore.emergency_contacts
      : Array.isArray(unitFromStore?.emergency_contact)
      ? unitFromStore.emergency_contact
      : [];
  }

  return { contacts, unitName: unit.description || unit.name || "" };
}

// Contexto de detección de la alarma en atención (cámara + tipo de detección),
// para pre-filtrar el módulo /detection_event.
function getAlarmDetectionContext(store) {
  const alarm = findAttendingAlarm(store);
  const det = alarm?.detection_event_data;
  if (!det) return null;
  const idCameraProxy =
    det.id_camera_proxy ??
    det.camera_data?.camera?.id_camera_proxy ??
    alarm?.cameras?.[0]?.camera_proxy_id ??
    null;
  if (!idCameraProxy) return null;
  return { idCameraProxy, eventType: det.event_type ?? null };
}

function getAdjacentTabId(tabs, activeTabId, direction) {
  const orderedIds = getOrderedTabIds(tabs);
  const currentIndex = orderedIds.indexOf(activeTabId);
  if (currentIndex === -1) return null;
  const nextIndex = direction === "prev" ? currentIndex - 1 : currentIndex + 1;
  if (nextIndex < 0 || nextIndex >= orderedIds.length) return null;
  return orderedIds[nextIndex];
}

const HOTKEY_MAP = [
  {
    id: "help",
    key: "F1",
    label: "Abrir ayuda",
    requiresAuth: false,
    action: ({ vm }) => {
      console.log("F1");
      vm.$root.$emit("hotkey:help");
    },
  },
  {
    id: "unit-search",
    code: "F3",
    // ctrl: true,
    // shift: true,
    label: "Busqueda de Inmueble",
    requiresAuth: true,
    ability: { action: "read", subject: "Units" },
    action: ({ store }) => {
      startPick({
        ghostHtml: "Suelta para colocar lista de instalaciones",
        onStart: () => {
          layoutApi.setSlotData(SLOT_TYPES.INFO, `INFO`, {showFilterUnits : true}, 'unit');
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "open-waze",
    code: "F4",
    ctrl: false,
    shift: false,
    label: "Abrir Waze",
    requiresAuth: true,
    // ability: { action: "read", subject: "User" },
    action: ({ store }) => {
      startPick({
        ghostHtml: "Suelta para abrir waze",
        onStart: () => {
          layoutApi.setSlotData(SLOT_TYPES.WAZE, `Waze`,{ data: null });
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "patrol-log-search",
    code: "KeyK",
    ctrl: true,
    shift: true,
    label: "Bitácora de unidades",
    requiresAuth: true,
    ability: { action: "read", subject: "PatrolLogs" },
    action: ({ store }) => {
      startPick({
        ghostHtml: "Suelta para colocar bitácora de unidades",
        onStart: () => {
          layoutApi.setSlotData(SLOT_TYPES.DRAGGABLE_MENU, `patrol_logs`, {
            filters: [],
            type: 'patrol_logs'
          });
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "alarm-history",
    code: "KeyH",
    ctrl: true,
    shift: true,
    // alt: true,
    label: "Ir al Historial de Alarmas",
    requiresAuth: true,
    ability: { action: "read", subject: "Alarm" },
    action: ({ store }) => {
      const unit = findFocusedUnit(store);
      const dateRange = getLast30DaysRange();
      startPick({
        ghostHtml: "Suelta para colocar lsta de alarmas",
        onStart: () => {
          layoutApi.setSlotData(SLOT_TYPES.DRAGGABLE_MENU, `alarm`, {
            filters: {
              ...(unit?.id ? { unit: [unit.id] } : {}),
              startDate: dateRange.start,
              endDate: dateRange.end,
            },
            type: 'alarm'
          });
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "operational-security-form-answers",
    code: "KeyF",
    ctrl: true,
    shift: true,
    label: "Acceso a módulo BAU (Formularios)",
    requiresAuth: true,
    ability: { action: "read", subject: "OpSecurityFormAnswers" },
     action: ({ store }) => {
      startPick({
        ghostHtml: "Suelta para colocar formularios de seguridad",
        onStart: () => {
          layoutApi.setSlotData(SLOT_TYPES.DRAGGABLE_MENU, `operational_processes`, {
            filters: [],
            type: 'operational_processes'
          });
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "external-personnel-access-control-total",
    code: "KeyM",
    ctrl: true,
    shift: true,
    label: "Control de acceso de personal externo - mantenimiento total",
    requiresAuth: true,
    ability: { action: "read", subject: "ExternalPersonnelAccessControl" },
    action: ({ store }) => {
      startPick({
        ghostHtml: "Suelta para colocar control de acceso de personal externo",
        onStart: () => {
          layoutApi.setSlotData(
            SLOT_TYPES.DRAGGABLE_MENU,
            "external_personnel_access_control",
            {
              filters: {
                created_at: moment().format("YYYY-MM-DD"),
                type_of_maintenance: "Total",
              },
              type: "external_personnel_access_control",
            }
          );
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "external-personnel-access-control-partial",
    code: "KeyN",
    ctrl: true,
    shift: true,
    label: "Control de acceso de personal externo - mantenimiento parcial",
    requiresAuth: true,
    ability: { action: "read", subject: "ExternalPersonnelAccessControl" },
    action: ({ store }) => {
      startPick({
        ghostHtml: "Suelta para colocar control de acceso de personal externo parcial",
        onStart: () => {
          layoutApi.setSlotData(
            SLOT_TYPES.DRAGGABLE_MENU,
            "external_personnel_access_control",
            {
              filters: {
                created_at: moment().format("YYYY-MM-DD"),
                type_of_maintenance: "Parcial",
              },
              type: "external_personnel_access_control",
            }
          );
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "external-personnel-access-control-all",
    code: "F2",
    // ctrl: true,
    // alt: true,
    // shift: true,
    label: "Control de acceso de personal externo - todos los mantenimientos",
    requiresAuth: true,
    ability: { action: "read", subject: "ExternalPersonnelAccessControl" },
    action: ({ store }) => {
      startPick({
        ghostHtml: "Suelta para colocar control de acceso de personal externo",
        onStart: () => {
          layoutApi.setSlotData(
            SLOT_TYPES.DRAGGABLE_MENU,
            "external_personnel_access_control",
            {
              filters: {
                created_at: moment().format("YYYY-MM-DD"),
              },
              type: "external_personnel_access_control",
            }
          );
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "operational-security-new-form-answer",
    code: "KeyQ",
    ctrl: true,
    shift: true,
    label: "Crear formulario de seguridad",
    requiresAuth: true,
    ability: { action: "create", subject: "OpSecurityFormAnswers" },
    action: ({ store }) => {
      startPick({
        ghostHtml: "Suelta para colocar el formulario de seguridad operativa",
        onStart: () => {
          layoutApi.setSlotData(SLOT_TYPES.DRAGGABLE_MENU, `operational_processes`, {
            filters: [],
            type: 'operational_processes',
            autoCreate: true,
          });
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "directory-search",
    code: "KeyS",
    ctrl: true,
    shift: true,
    label: "Busqueda de directorio",
    requiresAuth: true,
    ability: { action: "read", subject: "Directory" },
    action: ({ store }) => {
      startPick({
        ghostHtml: "Suelta para colocar directorio",
        onStart: () => {
          layoutApi.setSlotData(SLOT_TYPES.DRAGGABLE_MENU, `directory`, {
            filters: [],
            type: 'directory'
          });
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "operator-user-manual",
    code: "F8",
    label: "Abrir manual de usuario operador",
    requiresAuth: false,
    action: ({ vm }) => {
      vm.$root.$emit("hotkey:operator-user-manual");
    },
  },
  {
    id: "omnicloud-face-recognition",
    code: "KeyA",
    ctrl: true,
    shift: true,
    label: "Reconocimiento facial Omnicloud",
    requiresAuth: true,
    ability: { action: "read", subject: "OmnicloudFrFaces" },
    action: ({ store }) => {
      startPick({
        ghostHtml: "Suelta para colocar reconocimiento facial",
        onStart: () => {
          layoutApi.setSlotData(
            SLOT_TYPES.DRAGGABLE_MENU,
            "omnicloud_face_recognition",
            { filters: [], type: "omnicloud_face_recognition" }
          );
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "unit-log-search",
    code: "KeyL",
    ctrl: true,
    shift: true,
    label: "Registro de unidades",
    requiresAuth: true,
    ability: { action: "read", subject: "UnitLogs" },
    action: ({ store }) => {
      startPick({
        ghostHtml: "Suelta para colocar registro de unidades",
        onStart: () => {
          layoutApi.setSlotData(SLOT_TYPES.DRAGGABLE_MENU, `unit_log`, {
            filters: [],
            type: 'unit_log'
          });
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "facility-search",
    code: "KeyU",
    ctrl: true,
    shift: true,
    label: "Busqueda de sucursales",
    requiresAuth: true,
    ability: { action: "read", subject: "Units" },
    action: async ({ vm, store }) => {
      try {
        await store.dispatch("facility_category/getAll", { max: 1000 });
      } catch (error) {
        vm.$toast({
          component: ToastificationContent,
          props: {
            title: "No fue posible cargar las categorías de instalaciones",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        return;
      }

      const categoryTexts = ["DATA CENTER", "CPAE", "EDIFICIO"];
      const categoryIds = store.getters["facility_category/getSelectedItems"]
        .filter(({ label }) => {
          const normalizedLabel = String(label).toUpperCase();
          return categoryTexts.some((text) => normalizedLabel.includes(text));
        })
        .map(({ value }) => value);

      if (!categoryIds.length) {
        vm.$toast({
          component: ToastificationContent,
          props: {
            title: "No se encontraron las categorías DATA CENTER, CPAE o EDIFICIO",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      startPick({
        ghostHtml: "Suelta para colocar instalaciones",
        onStart: () => {
          layoutApi.setSlotData(SLOT_TYPES.DRAGGABLE_MENU, `unit`, {
            filters: {
              category: categoryIds,
            },
            type: 'unit'
          });
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "dmp-logs",
    code: "KeyD",
    ctrl: true,
    shift: true,
    label: "Logs de DMP",
    requiresAuth: true,
    ability: { action: "read", subject: "Dmp" },
    action: ({ store }) => {
      const unit = findFocusedUnit(store);
      const dateRange = getLast30DaysRange();
      const facilityOption = unit?.id
        ? {
            id: unit.id,
            value: unit.id,
            label: unit.description || unit.name || unit.text || String(unit.id),
            description: unit.description || unit.name || unit.text || "",
          }
        : null;
      startPick({
        ghostHtml: "Suelta para colocar los logs de DMP",
        onStart: () => {
          layoutApi.setSlotData(SLOT_TYPES.DRAGGABLE_MENU, "Logs de DMP", {
            filters: {
              ...(facilityOption ? { facilitiesFilter: [facilityOption] } : {}),
              startDatetime: dateRange.start,
              endDatetime: dateRange.end,
            },
            type: "dmp_logs",
          });
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "hanwha-logs",
    code: "KeyX",
    ctrl: true,
    shift: true,
    label: "Logs de Hanwha",
    requiresAuth: true,
    ability: { action: "read", subject: "HanwhaCamera" },
    action: ({ store }) => {
      const unit = findFocusedUnit(store);
      const dateRange = getLast30DaysRange();
      const unitIds = unit
        ? [unit.id, unit.id_unit].filter((id, index, ids) =>
            id != null && ids.findIndex(candidate => String(candidate) === String(id)) === index
          )
        : [];
      startPick({
        ghostHtml: "Suelta para colocar los logs de Hanwha",
        onStart: () => {
          layoutApi.setSlotData(SLOT_TYPES.DRAGGABLE_MENU, "Logs de Hanwha", {
            filters: {
              ...(unitIds.length ? { unit: unit.id, unitIds } : {}),
              startDatetime: dateRange.start,
              endDatetime: dateRange.end,
            },
            type: "hanwha_logs",
          });
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "reports-widget",
    code: "KeyL",
    ctrl: true,
    alt: true,
    label: "Widget de reportes",
    requiresAuth: true,
    ability: { action: "reporting", subject: "Widget" },
    action: ({ store }) => {
      startPick({
        ghostHtml: "Suelta para colocar el widget de reportes",
        onStart: () => {
          layoutApi.setSlotData(SLOT_TYPES.CHARTS_EMBEDDER, "Charts Embedder", {
            baseUrl: window.__env?.VUE_APP_STREAMLIT,
            lang: "es",
            live: false,
            refreshInterval: 15,
            token: window.__env?.VUE_APP_STREAMLIT_TOKEN,
          });
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "unit-cameras-new-tab",
    code: "KeyV",
    ctrl: true,
    shift: true,
    label: "Ver cámaras de la instalación con foco",
    highlight: true,
    requiresAuth: true,
    ability: { action: "live_video", subject: "Video" },
    when: ({ store }) => !!findFocusedUnit(store)?.id,
    action: ({ vm, store }) => {
      const focusedUnit = findFocusedUnit(store);
      if (!focusedUnit?.id) return;

      // Mismo origen que el árbol de units: el unit del store del grid con sus cámaras
      const unitFromStore = (store.state.grid.units || []).find(
        (unit) => String(unit.id) === String(focusedUnit.id)
      ) || focusedUnit;
      const cameras = Array.isArray(unitFromStore?.cameras) ? unitFromStore.cameras : [];

      if (cameras.length === 0) {
        vm.$toast({
          component: ToastificationContent,
          props: {
            title: "La unidad no tiene cámaras asignadas",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      // Misma forma de payload que el "unit-live" del árbol de subsistemas
      store.dispatch("grid/setUnitCamerasView", {
        data: unitFromStore,
        cameras,
      });
    },
  },
  {
    id: "alarm-dmp-logs-new-tab",
    code: "KeyG",
    ctrl: true,
    shift: true,
    label: "Logs del panel de alarmas (alarma en atención)",
    highlight: true,
    requiresAuth: true,
    ability: { action: "read", subject: "Dmp" },
    when: ({ store }) => !!findAttendingAlarm(store),
    action: ({ store }) => {
      const alarm = findAttendingAlarm(store);
      if (!alarm?.id) return;

      store.dispatch("grid/setAlarmDmpLogsView", { alarm });
    },
  },
  {
    id: "alarm-unit-floor-plans",
    code: "KeyP",
    ctrl: true,
    shift: true,
    label: "Planos de la instalación con foco",
    highlight: true,
    requiresAuth: true,
    ability: { action: "read", subject: "Units" },
    when: ({ store }) => !!findFocusedUnit(store)?.id,
    action: ({ store }) => {
      const alarm = findAttendingAlarm(store);
      const unit = findFocusedUnit(store);
      if (!unit?.id) return;

      const unitFromStore = (store.state.grid.units || []).find(
        item => String(item.id) === String(unit.id)
      );
      const unitName = unit.description || unit.name || unitFromStore?.description || "";
      const planesUrl = alarm?.unit?.id && String(alarm.unit.id) === String(unit.id)
        ? alarm.unit_planes || unit.unit_planes || unitFromStore?.unit_planes
        : unit.unit_planes || unitFromStore?.unit_planes;

      startPick({
        ghostHtml: "Suelta para colocar los planos de la instalación",
        onStart: () => {
          layoutApi.setSlotData(
            SLOT_TYPES.PLANE,
            `Planos: ${unitName}`,
            { planes_url: planesUrl, unit_id: unit.id }
          );
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "emergency-contacts",
    code: "KeyE",
    ctrl: true,
    shift: true,
    label: "Contactos de emergencia de la instalación con foco",
    highlight: true,
    requiresAuth: true,
    ability: { action: "read", subject: "Units" },
    when: ({ store }) => !!findFocusedUnit(store)?.id,
    action: ({ store }) => {
      // Snapshot de los contactos de la instalación con foco al presionar el hotkey
      const { contacts, unitName } = getFocusedUnitEmergencyContacts(store);
      startPick({
        ghostHtml: "Suelta para colocar contactos de emergencia",
        onStart: () => {
          layoutApi.setSlotData(
            SLOT_TYPES.EMERGENCY_CONTACTS,
            "Contactos de emergencia",
            { type: "emergency_contacts", contacts, unitName }
          );
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "detection-events-camera",
    code: "KeyY",
    ctrl: true,
    shift: true,
    label: "Detecciones de la cámara (alarma en atención)",
    highlight: true,
    requiresAuth: true,
    ability: { action: "read", subject: "DetectionEvent" },
    when: ({ store }) => !!getAlarmDetectionContext(store),
    action: ({ store }) => {
      const ctx = getAlarmDetectionContext(store);
      if (!ctx) return;

      const to = moment().format("YYYY-MM-DD HH:mm");
      const from = moment().subtract(30, "days").format("YYYY-MM-DD HH:mm");

      startPick({
        ghostHtml: "Suelta para colocar detecciones de la cámara",
        onStart: () => {
          layoutApi.setSlotData(SLOT_TYPES.DRAGGABLE_MENU, "Detecciones", {
            type: "detection_event",
            filters: {
              id_camera_proxy: [ctx.idCameraProxy],
              event_type: ctx.eventType ? [ctx.eventType] : null,
              from,
              to,
            },
          });
          store.dispatch("grid/updateUserConfig");
        },
        onEnd: () => {},
      });
    },
  },
  {
    id: "tab-prev",
    code: "ArrowLeft",
    key: "←",
    alt: true,
    label: "Pestaña anterior",
    route: "/inicio",
    requiresAuth: true,
    action: ({ store }) => {
      const tabs = store.state.grid.tabs;
      const activeTabId = store.state.grid.activeTab;
      const targetId = getAdjacentTabId(tabs, activeTabId, "prev");
      if (targetId) store.dispatch("grid/setActiveTab", targetId);
    },
  },
  {
    id: "tab-next",
    code: "ArrowRight",
    key: "→",
    alt: true,
    label: "Pestaña siguiente",
    route: "/inicio",
    requiresAuth: true,
    action: ({ store }) => {
      const tabs = store.state.grid.tabs;
      const activeTabId = store.state.grid.activeTab;
      const targetId = getAdjacentTabId(tabs, activeTabId, "next");
      if (targetId) store.dispatch("grid/setActiveTab", targetId);
    },
  },
];

function matchesShortcut(e, shortcut) {
  const keyMatch = shortcut.code ? e.code === shortcut.code : e.key === shortcut.key;

  const ctrlMatch = !!shortcut.ctrl === (e.ctrlKey || e.metaKey);
  const shiftMatch = !!shortcut.shift === e.shiftKey;
  const altMatch = !!shortcut.alt === e.altKey;

  return keyMatch && ctrlMatch && shiftMatch && altMatch;
}

function isInputFocused(e) {
  const tag = e.target.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    e.target.isContentEditable
  );
}

function canExecute(shortcut, authenticated = null) {
  // Auth check
  if (shortcut.requiresAuth !== false) {
    const isLoggedIn = authenticated === null ? isUserLoggedIn() : authenticated;
    if (!isLoggedIn || (typeof isLoggedIn === "string" && isLoggedIn.length === 0)) {
      return false;
    }
  }

  if (shortcut.ability) {
    if (!ability.can(shortcut.ability.action, shortcut.ability.subject)) {
      return false;
    }
  }

  // Condición dinámica (ej. solo con alarma en atención)
  if (typeof shortcut.when === "function" && !shortcut.when({ store })) {
    return false;
  }

  return true;
}

export function useGlobalHotkeys(vm) {
  function handleKeydown(e) {
    // Si estamos en un input, solo permitir combos con modificadores
    if (isInputFocused(e)) {
      const hasModifier = e.ctrlKey || e.metaKey || e.altKey;
      if (!hasModifier && !e.key.startsWith("F")) return;
    }

    for (const shortcut of HOTKEY_MAP) {
      if (!matchesShortcut(e, shortcut)) continue;
      if (!canExecute(shortcut)) continue;
      if (shortcut.route && router.currentRoute.path !== shortcut.route) continue;

      e.preventDefault();
      e.stopPropagation();
      shortcut.action({ vm, store, router });
      return;
    }
  }

  onMounted(() => {
    window.addEventListener("keydown", handleKeydown, { capture: true });
  });

  onUnmounted(() => {
    window.removeEventListener("keydown", handleKeydown, { capture: true });
  });

  const availableHotkeys = computed(() => {
    // Vuex is reactive; unlike localStorage, this value changes immediately
    // after login and forces the hotkey list to be recalculated.
    const authenticated = Object.keys(store.state.user.user || {}).length > 0;
    return HOTKEY_MAP.filter(shortcut => canExecute(shortcut, authenticated));
  });

  return { availableHotkeys, HOTKEY_MAP };
}
